import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { get } from 'lodash';
import { Grid } from '@material-ui/core';
import { Field, FieldArray, Form, Formik } from 'formik';
import { array, object, string } from 'yup';
import { useTranslation } from '../../../../../src/i18n';
import { PRODUCTION_DOCUMENT_TYPES, TP } from '../../../../../constants/consts';
import SubmitModalLayout from '../../../../../components/SubmitModalLayout';
import FormikOnError from '../../../../../components/Fields/FormikOnError';
import Title from '../../../../../components/Title';
import AddAnotherField from '../../../../../components/AddAnotherField';
import AddAnotherFieldButton from '../../../../../components/AddAnotherField/AddAnotherFieldButton';
import { getOption, romanize } from '../../../../../utils/common';
import TimePicker from '../../../../../components/Fields/TimePicker';
import TextArea from '../../../../../components/Fields/TextArea';
import LanguageSelect from '../../../../../components/LanguageSelect';
import EditModeCard, { EditModeCardSection } from '../../../../../components/EditModeCard';
import NoListItems from '../../../../../components/NoListItems';
import { SaveButtonBlock } from '../../../../../components/EditComponents/SaveButtonBlock';
import { isFocusedAddAnotherField } from '../../../../../utils/editCommon';

const useStyles = makeStyles(theme => ({
  content: {
    padding: theme.spacing(2),
  },
  docCard: {
    marginBottom: theme.spacing(4),
  },
  inputClassName: {
    maxWidth: 'unset',
  },
}));

export const actFields = {
  runTime: 'act_run_time',
  intermissionTime: 'act_intermission_time',
  content: 'content',
  number: 'act_number',
};

const actEmptyValue = (actNumber, id) => ({
  [actFields.runTime]: '',
  [actFields.intermissionTime]: '',
  [actFields.content]: '',
  [actFields.number]: actNumber,
  id,
});

export const synopsisFields = {
  lang: {
    name: 'lang',
    label: `${TP}.SELECT_LANGUAGE`,
  },
  synopsis: {
    name: 'synopsis',
    label: `${TP}.FN_ENTER_SYNOPSIS`,
    placeholder: `${TP}.FN_ENTER_PRODUCTION_DESCRIPTION`,
    document_type: PRODUCTION_DOCUMENT_TYPES.SYNOPSIS,
  },
};

export const synopsisEmptyValue = synopsis => ({
  [synopsisFields.lang.name]: synopsis?.language ? getOption(synopsis?.language) : null,
  [synopsisFields.synopsis.name]: {
    id: synopsis?.id,
    [actFields.content]: get(synopsis, actFields.content),
    [actFields.runTime]: get(synopsis, actFields.runTime),
  },
});

const AddSynopsisModal = ({ onClose, document, loadingFieldName, onSubmit, langs }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const SynopsisFormRef = React.createRef(null);

  const getInitFormValues = React.useCallback(() => {
    const synopsis = get(document, 'synopsis');
    return {
      ...synopsisEmptyValue(synopsis),
      acts: document?.acts?.map(act => ({
        id: act?.id,
        [actFields.content]: get(act, actFields.content),
        [actFields.intermissionTime]: get(act, actFields.intermissionTime),
        [actFields.runTime]: get(act, actFields.runTime),
        [actFields.number]: get(act, actFields.number),
      })),
    };
  }, [document]);

  return (
    <SubmitModalLayout
      isOpened
      onClose={onClose}
      title1={t(`${TP}.FN_UPDATE_SYNOPSIS`)}
      footer={
        <SaveButtonBlock
          onSaveClick={() => {
            // eslint-disable-next-line no-unused-expressions
            SynopsisFormRef?.current?.submitForm();
          }}
          updatedDate={document?.synopsis?.updated_at}
          buttonText={t(`${TP}.m_SAVE`)}
          isLoading={loadingFieldName === 'synopsis'}
        />
      }
    >
      <Formik
        initialValues={getInitFormValues()}
        enableReinitialize
        onSubmit={onSubmit}
        validationSchema={ProductionSynopsisSchema}
        validateOnMount
        innerRef={SynopsisFormRef}
      >
        {({ values, setFieldValue }) => (
          <Form className={classes.content}>
            <FormikOnError withFocus>
              <EditModeCard className={classes.docCard}>
                <EditModeCardSection first>
                  <Title className={classes.actTitle}>{t(`${TP}.FN_PRODUCTION_SYNOPSIS`)}</Title>
                  <Grid container spacing={2} direction="column">
                    <Grid item>
                      <Field
                        name={synopsisFields.lang.name}
                        langs={langs}
                        label={t(synopsisFields.lang.label)}
                        formikUse
                        component={LanguageSelect}
                      />
                    </Grid>
                    <Grid item>
                      <Field
                        component={TextArea}
                        name={`${synopsisFields.synopsis.name}.${actFields.content}`}
                        label={t(synopsisFields.synopsis.label)}
                        placeholder={t(synopsisFields.synopsis.placeholder)}
                        minLines={10}
                      />
                    </Grid>
                    <Grid item>
                      <Field
                        component={TimePicker}
                        name={`${synopsisFields.synopsis.name}.${actFields.runTime}`}
                        label={t(`${TP}.FN_PRODUCTION_RUN_TIME`)}
                        onTimeChange={time =>
                          setFieldValue(`${synopsisFields.synopsis.name}.${actFields.runTime}`, time)
                        }
                      />
                    </Grid>
                  </Grid>
                </EditModeCardSection>
              </EditModeCard>
              <EditModeCard className={classes.docCard}>
                <FieldArray
                  name="acts"
                  render={arrayHelpers => {
                    const addFunc = index =>
                      arrayHelpers.push(
                        actEmptyValue(index, document?.acts?.find(a => a[actFields.number] === index)?.id),
                      ); // to set id of act if it was added before
                    return values?.acts?.length > 0 ? (
                      values?.acts.map((field, index) => (
                        <EditModeCardSection key={field[actFields.number]} first={index === 0}>
                          <Title className={classes.actTitle}>
                            {t(`${TP}.FN_ACT`)}
                            {` ${romanize(get(values, `acts.${index}.${actFields.number}`))}`}
                          </Title>
                          <AddAnotherField
                            values={values.acts}
                            removeFunc={() => arrayHelpers.remove(index)}
                            addFunc={() => addFunc(index + 2)}
                            isLast={index === values.acts.length - 1}
                            addButtonText={t(`${TP}.FN_ADD_ACT`)}
                            inputClassName={classes.inputClassName}
                            addDisabled={field[actFields.content]?.length === 0}
                            withDelete
                            addButtonPosition="bottom"
                          >
                            <Grid item style={{ marginBottom: 10 }} xs={12} sm={12}>
                              <Field
                                autoFocus={isFocusedAddAnotherField(index, values.acts)}
                                name={`acts.${index}.${actFields.content}`}
                                component={TextArea}
                                label={t(`${TP}.FN_ENTER_ACT_DESCRIPTION`)}
                              />
                            </Grid>
                            <Grid item container spacing={2}>
                              <Grid item>
                                <Field
                                  component={TimePicker}
                                  name={`acts.${index}.${actFields.runTime}`}
                                  label={t(`${TP}.FN_ACT_RUN_TIME`)}
                                  onTimeChange={time => setFieldValue(`acts.${index}.${actFields.runTime}`, time)}
                                />
                              </Grid>
                              <Grid item>
                                <Field
                                  component={TimePicker}
                                  name={`acts.${index}.${actFields.intermissionTime}`}
                                  label={t(`${TP}.FN_ACT_INTERMISSION_TIME`)}
                                  onTimeChange={time =>
                                    setFieldValue(`acts.${index}.${actFields.intermissionTime}`, time)
                                  }
                                />
                              </Grid>
                            </Grid>
                          </AddAnotherField>
                        </EditModeCardSection>
                      ))
                    ) : (
                      <EditModeCardSection first>
                        <NoListItems text={t(`${TP}.FN_NO_ACTS_ADDED`)} />
                        <AddAnotherFieldButton onClick={() => addFunc(1)} addButtonText={t(`${TP}.FN_ADD_ACT`)} />
                      </EditModeCardSection>
                    );
                  }}
                />
              </EditModeCard>
            </FormikOnError>
          </Form>
        )}
      </Formik>
    </SubmitModalLayout>
  );
};

AddSynopsisModal.propTypes = {
  loadingFieldName: PropTypes.string,
};

export default memo(AddSynopsisModal);

const ProductionSynopsisSchema = () =>
  object().shape({
    [synopsisFields.synopsis.name]: object().shape({
      [actFields.content]: string().required(`${TP}.FN_REQUIRED_FIELD`),
    }),
    acts: array().of(
      object().shape({
        [actFields.content]: string().required(`${TP}.FN_REQUIRED_FIELD`),
      }),
    ),
    [synopsisFields.lang.name]: object()
      .nullable()
      .required(`${TP}.FN_REQUIRED_FIELD`),
  });
